'use client';

import React from 'react';
import { useRouter } from 'next/navigation';

interface AnonymousStateProps {
  /** Closes the drawer before navigating to sign-up */
  onBeforeNavigate?: () => void;
}

type Step = { key: string; content: React.ReactNode };

const STEPS: Step[] = [
  {
    key: 'start',
    content: (
      <>
        Start your first plan and <strong>pay small small</strong>.
      </>
    ),
  },
  {
    key: 'build',
    content: (
      <>
        Every <strong>on-time payment</strong> builds your{' '}
        <strong>trust level</strong> with us.
      </>
    ),
  },
  {
    key: 'earn',
    content: (
      <>
        Once you&apos;ve built enough trust, you can{' '}
        <strong>collect before full payment</strong> on your next plan.
      </>
    ),
  },
];

/**
 * Anonymous drawer state.
 *
 * Mirrors Pencil frame `USYEx`. Shown when a signed-out visitor taps
 * "Learn more →" on the PDP Pay Small Small card. No score card or
 * earn rows (anon has no record), primary CTA routes to sign-up.
 */
export function AnonymousState({ onBeforeNavigate }: AnonymousStateProps) {
  const router = useRouter();

  const handleSignUp = () => {
    onBeforeNavigate?.();
    router.push('/sign-up');
  };

  return (
    <div className="flex flex-col gap-[18px]">
      {/* Hero card */}
      <div className="flex flex-col gap-2.5 rounded-xl bg-[#F8FAFC] p-4">
        <p className="text-[15px] font-extrabold leading-[1.2] text-[#0C0C0C]">
          Collect before you finish paying.
        </p>
        <p className="text-[13px] font-medium leading-[1.5] text-[#0C0C0C]">
          Trusted customers can collect before full payment. This is a
          privileged service that becomes available to you once you&apos;ve
          earned it.
        </p>
      </div>

      {/* How it works */}
      <div className="flex flex-col gap-1.5">
        <p className="text-[14px] font-extrabold text-[#0C0C0C]">
          How it works
        </p>
        {STEPS.map((step, index) => (
          <div key={step.key} className="flex items-start gap-2.5 py-2">
            <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-motitoBlue">
              <span className="text-[12px] font-extrabold text-white">
                {index + 1}
              </span>
            </div>
            <p className="pt-0.5 text-[13px] font-medium leading-[1.4] text-[#374151]">
              {step.content}
            </p>
          </div>
        ))}
      </div>

      {/* Sign up CTA */}
      <button
        type="button"
        onClick={handleSignUp}
        className="w-full rounded-full bg-[#0C0C0C] px-4 py-3.5 text-[14px] font-bold text-white transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-motitoBlue focus-visible:ring-offset-2"
      >
        Sign up to start
      </button>
    </div>
  );
}

export default AnonymousState;
